import { useEffect, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import classNames from "classnames";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { FilterMatchMode, FilterService } from "primereact/api";

// Case-insensitive IN filter: stored language values may be mixed case (e.g. "C#", "C++").
// Using a registered filter (not CUSTOM mode) ensures PrimeReact passes the full selection
// array as one argument, giving OR semantics across multiple selected values.
FilterService.register("inCaseInsensitive", (value: unknown, filter: unknown) => {
  if (!filter || !Array.isArray(filter) || filter.length === 0) return true;
  if (typeof value !== "string") return false;
  const filterArr = filter as string[];
  const valueLower = value.toLowerCase();
  if (filterArr.includes(valueLower)) return true;
  // "other" is a catch-all: also matches values not in the known language list.
  if (filterArr.includes("other") && !KNOWN_LANGUAGE_VALUES.has(valueLower)) return true;
  return false;
});
// Array-contains-any filter for topics/tooling: matches rows where the field array
// contains at least one of the selected filter values (OR semantics across a stored array).
FilterService.register("topicsContainsAny", (value: unknown, filter: unknown) => {
  if (!filter || (Array.isArray(filter) && (filter as string[]).length === 0)) return true;
  if (!Array.isArray(value) || !Array.isArray(filter)) return false;
  return (filter as string[]).some(f => (value as string[]).includes(f));
});
import { DataTable } from "primereact/datatable";
import type { DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import CsvUploadModal from "@/components/assignments/CsvUploadModal";
import type { DirectoryUserShape, StoredUser } from "@/api/assignments";
import type { AvailableUsersMap } from "@/hooks/useAvailableUsers";
import type { MutableRefObject } from "react";
import {
  textFilterElement,
  multiSelectOnChangeFilterElement,
  triStateBooleanFilterElement,
} from "@/helpers/tableFilters";
import {
  devProfileLanguage,
  devProfileExperience,
  devProfileExpertise,
  devProfileTopics,
  devProfileAiStackCategory,
  devProfileAdditionalLanguages,
  AI_STACK_COLUMNS,
} from "@/helpers/developerProfile";
import { proficiencyDotBody, scoreProgressBody, statusBody } from "@/helpers/tableBody";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { startCase } from "lodash";
import { STATUS_CONFIG, STATUS_LABEL_MAP } from "sj-ui-toolkit/src/constants/assessmentStatus";

// ─── Column definitions ───────────────────────────────────────────────────────

// Known language values for the "Other" catch-all filter.
const KNOWN_LANGUAGE_VALUES = new Set(
  DeveloperProfileConstants.OPTIONS.programmingLanguages.map(o => o.value)
);

// Fields that only carry data in the assessment (recommended assignment) flow.
// Hidden from the column picker when hasPrepopulatedUsers is false.
const ASSESSMENT_ONLY_FIELDS = new Set(["averageScore", "proficiency", "status"]);

const audienceStatusBody = statusBody<DirectoryUserShape>(row => {
  const statusKey = row.status;
  const config = statusKey ? STATUS_CONFIG[statusKey] : undefined;
  const colorClass = config?.color === "warning" ? themeClasses.textWarning : (config?.color ?? "");
  const label = statusKey ? (STATUS_LABEL_MAP[statusKey] ?? startCase(statusKey)) : "";
  return { icon: config?.icon || undefined, colorClass: colorClass || undefined, label };
});

const STATIC_COLUMNS = [
  {
    field: "email",
    header: "Email",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by email",
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "name.givenName",
    header: "First Name",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by first name",
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "name.familyName",
    header: "Last Name",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by last name",
    matchMode: FilterMatchMode.STARTS_WITH,
  },
];

const CONFIGURABLE_COLUMNS = [
  {
    field: "attributes.employeeNumber",
    header: "Employee #",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.jobRole",
    header: "Job Role",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by job role",
    visibleInitially: true,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.country",
    header: "Country",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by country",
    visibleInitially: true,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.department",
    header: "Department",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by department",
    visibleInitially: true,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.managersEmail",
    header: "Manager's Email",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by manager's email",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.managersName",
    header: "Manager",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by manager",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.teamName",
    header: "Team",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by team",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.title",
    header: "Title",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by title",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "attributes.businessUnit",
    header: "Business Unit",
    visibleInitially: false,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  {
    field: "developerProfile.jobFunction",
    header: "Job Function",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by job function",
    visibleInitially: false,
    matchMode: FilterMatchMode.CONTAINS,
  },
  {
    field: "developerProfile.language",
    header: "Programming Language",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select language",
    visibleInitially: false,
    matchMode: "inCaseInsensitive" as FilterMatchMode,
    body: (rowData: DirectoryUserShape) => devProfileLanguage(rowData.developerProfile),
  },
  {
    field: "developerProfile.experience",
    header: "Development Experience",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select experience",
    visibleInitially: false,
    matchMode: FilterMatchMode.IN,
    body: (rowData: DirectoryUserShape) => devProfileExperience(rowData.developerProfile),
  },
  {
    field: "developerProfile.expertise",
    header: "Technical Expertise",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select expertise",
    visibleInitially: false,
    matchMode: FilterMatchMode.IN,
    body: (rowData: DirectoryUserShape) => devProfileExpertise(rowData.developerProfile),
  },
  {
    field: "developerProfile.topics",
    header: "Tooling",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select tooling",
    visibleInitially: false,
    matchMode: "topicsContainsAny" as FilterMatchMode,
    body: (rowData: DirectoryUserShape) => devProfileTopics(rowData.developerProfile),
  },
  ...AI_STACK_COLUMNS.map(c => ({
    field: c.field,
    header: c.header,
    sortable: true,
    filter: true,
    filterPlaceholder: `Select ${c.filterNoun}`,
    visibleInitially: false,
    matchMode: "topicsContainsAny" as FilterMatchMode,
    body: (rowData: DirectoryUserShape) =>
      devProfileAiStackCategory(rowData.developerProfile, c.key),
  })),
  {
    field: "developerProfile.additionalLanguages",
    header: "Additional Languages",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select languages",
    visibleInitially: false,
    matchMode: "topicsContainsAny" as FilterMatchMode,
    body: (rowData: DirectoryUserShape) => devProfileAdditionalLanguages(rowData.developerProfile),
  },
  {
    field: "attributes.securityChampion",
    header: "Security Champion",
    sortable: true,
    filter: true,
    filterPlaceholder: "Select",
    visibleInitially: false,
    matchMode: FilterMatchMode.EQUALS,
    body: (rowData: DirectoryUserShape) =>
      rowData.attributes?.securityChampion === true
        ? "Yes"
        : rowData.attributes?.securityChampion === false
          ? "No"
          : "",
  },
  {
    field: "averageScore",
    header: "Avg Score",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by score",
    visibleInitially: false,
    matchMode: FilterMatchMode.EQUALS,
    body: scoreProgressBody<DirectoryUserShape>("averageScore"),
  },
  {
    field: "proficiency",
    header: "Proficiency",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by proficiency",
    visibleInitially: false,
    matchMode: FilterMatchMode.CONTAINS,
    body: proficiencyDotBody<DirectoryUserShape>("proficiency"),
  },
  {
    field: "status",
    header: "Status",
    sortable: true,
    filter: true,
    filterPlaceholder: "Search by status",
    visibleInitially: true,
    matchMode: FilterMatchMode.CONTAINS,
    body: audienceStatusBody,
  },
];

const buildInitialFilters = () => ({
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  email: { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "name.givenName": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "name.familyName": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.employeeNumber": {
    value: null as string | null,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  "attributes.jobRole": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.country": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.department": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.managersEmail": {
    value: null as string | null,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  "attributes.managersName": {
    value: null as string | null,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  "attributes.teamName": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.title": { value: null as string | null, matchMode: FilterMatchMode.STARTS_WITH },
  "attributes.businessUnit": {
    value: null as string | null,
    matchMode: FilterMatchMode.STARTS_WITH,
  },
  "developerProfile.jobFunction": {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  "developerProfile.language": {
    value: null as string[] | null,
    matchMode: "inCaseInsensitive" as FilterMatchMode,
  },
  "developerProfile.experience": {
    value: null as string[] | null,
    matchMode: FilterMatchMode.IN,
  },
  "developerProfile.expertise": { value: null as string[] | null, matchMode: FilterMatchMode.IN },
  "developerProfile.topics": {
    value: null as string[] | null,
    matchMode: "topicsContainsAny" as FilterMatchMode,
  },
  "attributes.securityChampion": {
    value: null as boolean | null,
    matchMode: FilterMatchMode.EQUALS,
  },
  ...Object.fromEntries(
    AI_STACK_COLUMNS.map(c => [
      c.field,
      { value: null as string[] | null, matchMode: "topicsContainsAny" as FilterMatchMode },
    ])
  ),
  "developerProfile.additionalLanguages": {
    value: null as string[] | null,
    matchMode: "topicsContainsAny" as FilterMatchMode,
  },
  averageScore: { value: null as number | null, matchMode: FilterMatchMode.EQUALS },
  proficiency: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
});

const GLOBAL_FILTER_FIELDS = [
  "email",
  "name.givenName",
  "name.familyName",
  "developerProfile.jobFunction",
  "developerProfile.language",
  "developerProfile.experience",
  "developerProfile.expertise",
  "developerProfile.topics",
  ...AI_STACK_COLUMNS.map(c => c.field),
  "developerProfile.additionalLanguages",
  "status",
];

const STATE_KEY_PREFIX = "audience-users-list";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AudienceUsersListProps {
  values: {
    users: StoredUser[];
    availableUsers: DirectoryUserShape[];
    filteredAvailableUsers?: DirectoryUserShape[];
    allUsers?: Map<string, AvailableUsersMap>;
  };
  handleChange: (values: {
    users: StoredUser[];
    availableUsers: DirectoryUserShape[];
    filteredAvailableUsers?: DirectoryUserShape[];
    allUsers?: Map<string, AvailableUsersMap>;
  }) => void;
  newHireValue?: boolean;
  handleNewHireChange?: (v: boolean) => void;
  showNewHire?: boolean;
  onSelectedUsersChange?: (users: DirectoryUserShape[]) => void;
  onAddUsersAndContinue?: MutableRefObject<(() => void) | null>;
  selectedUsersToAdd: DirectoryUserShape[];
  setSelectedUsersToAdd: (users: DirectoryUserShape[]) => void;
  selectedUsersToRemove: DirectoryUserShape[];
  setSelectedUsersToRemove: (users: DirectoryUserShape[]) => void;
  levelOptions?: Array<{ label: string; value: string }>;
  selectedLevel?: string[];
  onLevelChange?: (levels: string[]) => void;
  entityType?: string;
  hasPrepopulatedUsers?: boolean;
  tableId: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AudienceUsersList = forwardRef<{ handleSaveUsers: () => void }, AudienceUsersListProps>(
  (
    {
      values,
      handleChange,
      newHireValue = false,
      handleNewHireChange,
      showNewHire = true,
      onSelectedUsersChange,
      onAddUsersAndContinue,
      selectedUsersToAdd,
      setSelectedUsersToAdd,
      selectedUsersToRemove,
      setSelectedUsersToRemove,
      levelOptions = [],
      selectedLevel,
      onLevelChange,
      entityType = "Assignment",
      hasPrepopulatedUsers = false,
      tableId,
    },
    ref
  ) => {
    const stateKey = `${STATE_KEY_PREFIX}-${getCurrentSelectedTenant() ?? ""}-${tableId}`;

    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
    const [activeIndex, setActiveIndex] = useState(
      hasPrepopulatedUsers && (values.users?.length ?? 0) > 0 ? 1 : 0
    );
    const [filters, setFilters] = useState(buildInitialFilters);
    const [newHire, setNewHire] = useState(newHireValue);
    const [visibleColumns, setVisibleColumns] = useState(
      hasPrepopulatedUsers
        ? CONFIGURABLE_COLUMNS.filter(
            c => c.field === "averageScore" || c.field === "proficiency" || c.field === "status"
          )
        : CONFIGURABLE_COLUMNS.filter(c => c.visibleInitially)
    );

    const availableUsersSource = values.filteredAvailableUsers ?? values.availableUsers ?? [];
    const fullAvailableUsers = values.availableUsers ?? [];
    const storedUsers = values.users ?? [];

    // Lookup stored user in full available list (directory data with nested name).
    // Fall back to the stored object itself for pre-populated users that were already
    // removed from availableUsers (e.g. recommended assignment prepopulation).
    const addedUsers = useMemo(
      () =>
        storedUsers.map(su => {
          const found = fullAvailableUsers.find(u => u.directoryUuid === su.directoryUuid);
          return (found ?? su) as DirectoryUserShape;
        }),
      [fullAvailableUsers, storedUsers]
    );

    const filteredAvailableUsers = useMemo(() => {
      const addedUuids = new Set(storedUsers.map(u => u.directoryUuid));
      return availableUsersSource.filter(u => !addedUuids.has(u.directoryUuid ?? ""));
    }, [availableUsersSource, storedUsers]);

    useEffect(() => {
      onSelectedUsersChange?.(selectedUsersToAdd);
    }, [selectedUsersToAdd, onSelectedUsersChange]);

    const handleSaveUsers = () => {
      const newStored: StoredUser[] = [...storedUsers, ...selectedUsersToAdd.map(u => ({ ...u }))];
      const newAvailable = (values.availableUsers ?? []).filter(
        u => !selectedUsersToAdd.some(a => a.directoryUuid === u.directoryUuid)
      );
      setSelectedUsersToAdd([]);
      handleChange({ ...values, users: newStored, availableUsers: newAvailable });
    };

    useImperativeHandle(ref, () => ({ handleSaveUsers }));

    useEffect(() => {
      if (onAddUsersAndContinue) {
        onAddUsersAndContinue.current = handleSaveUsers;
      }
    }, [handleSaveUsers, onAddUsersAndContinue]);

    const handleRemoveUsers = () => {
      const newStored = storedUsers.filter(
        u => !selectedUsersToRemove.some(r => r.directoryUuid === u.directoryUuid)
      );
      const newAvailable = [
        ...(values.availableUsers ?? []),
        ...(selectedUsersToRemove as DirectoryUserShape[]),
      ];
      setSelectedUsersToRemove([]);
      handleChange({ ...values, users: newStored, availableUsers: newAvailable });
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters(prev => ({
        ...prev,
        global: { ...prev.global, value },
      }));
      setGlobalFilterValue(value);
    };

    const handleTabChange = (e: { index: number }) => {
      setFilters(buildInitialFilters());
      setGlobalFilterValue("");
      setSelectedUsersToAdd([]);
      setSelectedUsersToRemove([]);
      setActiveIndex(e.index);
    };

    const onColumnSelectChange = (e: { value: string[] }) => {
      setVisibleColumns(CONFIGURABLE_COLUMNS.filter(c => e.value.includes(c.field)));
    };

    const onNewHireChange = () => {
      const updated = !newHire;
      setNewHire(updated);
      handleNewHireChange?.(updated);
    };

    const onStateRestore = (state: DataTableStateEvent) => {
      const globalFilter = (
        state.filters as Record<string, { value?: string | null }> | undefined
      )?.["global"];
      setGlobalFilterValue(globalFilter?.value ?? "");
    };

    const onStateSave = () => {
      const stored = window.localStorage.getItem(stateKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Record<string, unknown>;
          delete parsed["selection"];
          window.localStorage.setItem(stateKey, JSON.stringify(parsed));
        } catch {
          // ignore
        }
      }
    };

    const renderHeader = () => (
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <IconField iconPosition="right">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search any field"
              aria-label="Search users"
              data-testid={`${tableId}-search-input`}
            />
          </IconField>
          <Button
            label={
              activeIndex === 0
                ? `Add ${selectedUsersToAdd.length} Users`
                : `Remove ${selectedUsersToRemove.length} Users`
            }
            size="small"
            data-testid={
              activeIndex === 0 ? `${tableId}-add-users-button` : `${tableId}-remove-users-button`
            }
            onClick={activeIndex === 0 ? handleSaveUsers : handleRemoveUsers}
          />
        </div>

        <div className="flex items-center gap-4">
          {levelOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="levelMultiselect">Level</label>
              <i className="pi pi-info-circle cursor-help" id="audience-level-tooltip" />
              <Tooltip target="#audience-level-tooltip" content="Filter users by level" />
              <MultiSelect
                inputId="levelMultiselect"
                value={selectedLevel ?? []}
                onChange={e => onLevelChange?.(e.value as string[])}
                options={levelOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Choose Levels"
                display="chip"
                className="w-48"
                maxSelectedLabels={1}
                selectedItemsLabel="{0} levels selected"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <label htmlFor="msAudienceColumns">Columns</label>
            <MultiSelect
              inputId="msAudienceColumns"
              value={visibleColumns.map(c => c.field)}
              onChange={onColumnSelectChange}
              options={CONFIGURABLE_COLUMNS.filter(
                c => hasPrepopulatedUsers || !ASSESSMENT_ONLY_FIELDS.has(c.field)
              )}
              optionLabel="header"
              optionValue="field"
              placeholder="Select Columns"
              className="w-48"
              maxSelectedLabels={2}
            />
          </div>
        </div>
      </div>
    );

    // Calls setFilters directly via multiSelectOnChangeFilterElement, bypassing PrimeReact's
    // filterApplyCallback stale-closure bug where onFilterApply fires with the previous
    // d_filtersState. Calling setFilters directly updates props.filters immediately; DataTable
    // picks it up via useUpdateEffect([props.filters]).
    const makeDevProfileFilter = (
      field: string,
      selectOptions: Array<{ label: string; value: string }>,
      matchMode: string,
      placeholder: string
    ) =>
      multiSelectOnChangeFilterElement(selectOptions, placeholder, value =>
        setFilters(
          prev =>
            ({
              ...prev,
              [field]: { value, matchMode: matchMode as FilterMatchMode },
            }) as ReturnType<typeof buildInitialFilters>
        )
      );
    const languageFilterElement = makeDevProfileFilter(
      "developerProfile.language",
      DeveloperProfileConstants.OPTIONS.programmingLanguages,
      "inCaseInsensitive",
      "Select language"
    );
    const experienceFilterElement = makeDevProfileFilter(
      "developerProfile.experience",
      DeveloperProfileConstants.OPTIONS.experienceLevels,
      FilterMatchMode.IN,
      "Select experience"
    );
    const expertiseFilterElement = makeDevProfileFilter(
      "developerProfile.expertise",
      DeveloperProfileConstants.OPTIONS.technicalExpertise,
      FilterMatchMode.IN,
      "Select expertise"
    );
    const topicsFilterElement = makeDevProfileFilter(
      "developerProfile.topics",
      DeveloperProfileConstants.OPTIONS.architectureTools,
      "topicsContainsAny",
      "Select tooling"
    );
    const aiStackFilterElements = Object.fromEntries(
      AI_STACK_COLUMNS.map(c => [
        c.field,
        makeDevProfileFilter(
          c.field,
          c.filterOptions,
          "topicsContainsAny",
          `Select ${c.filterNoun}`
        ),
      ])
    );
    const additionalLanguagesFilterElement = makeDevProfileFilter(
      "developerProfile.additionalLanguages",
      DeveloperProfileConstants.OPTIONS.additionalLanguages,
      "topicsContainsAny",
      "Select languages"
    );

    const allColumns = [
      ...STATIC_COLUMNS,
      ...CONFIGURABLE_COLUMNS.filter(
        c =>
          visibleColumns.some(v => v.field === c.field) &&
          (hasPrepopulatedUsers || !ASSESSMENT_ONLY_FIELDS.has(c.field))
      ),
    ];

    const renderTable = (
      data: DirectoryUserShape[],
      selection: DirectoryUserShape[],
      onSelectionChange: (users: DirectoryUserShape[]) => void
    ) => (
      <DataTable
        aria-label="Users list"
        data-testid={`${tableId}-users-table`}
        value={data}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        tableClassName="dt-responsive-text"
        globalFilterFields={GLOBAL_FILTER_FIELDS}
        filters={filters}
        onFilter={(e: DataTableStateEvent) =>
          setFilters(e.filters as ReturnType<typeof buildInitialFilters>)
        }
        selectionMode="multiple"
        selection={selection}
        onSelectionChange={e => onSelectionChange(e.value as DirectoryUserShape[])}
        sortField="name.familyName"
        sortOrder={1}
        removableSort
        metaKeySelection={false}
        stateStorage="local"
        stateKey={stateKey}
        onStateSave={onStateSave}
        onStateRestore={onStateRestore}
        header={renderHeader()}
        filterDisplay="menu"
        emptyMessage="No users found"
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "3rem" }}
          pt={{ rowCheckbox: { root: { "data-testid": `${tableId}-row-checkbox` } } }}
        />
        {allColumns.map(col => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            sortable={col.sortable}
            filter={col.filter}
            filterElement={
              col.filter
                ? col.field === "developerProfile.language"
                  ? languageFilterElement
                  : col.field === "developerProfile.experience"
                    ? experienceFilterElement
                    : col.field === "developerProfile.expertise"
                      ? expertiseFilterElement
                      : col.field === "developerProfile.topics"
                        ? topicsFilterElement
                        : col.field === "developerProfile.additionalLanguages"
                          ? additionalLanguagesFilterElement
                          : col.field === "attributes.securityChampion"
                            ? triStateBooleanFilterElement
                            : (aiStackFilterElements[col.field] ??
                              textFilterElement(col.filterPlaceholder ?? "Search..."))
                : undefined
            }
            filterFunction={
              (col as { filterFunction?: (value: unknown, filter: unknown) => boolean })
                .filterFunction
            }
            filterMatchMode={(col as { matchMode?: FilterMatchMode }).matchMode}
            showFilterMatchModes={false}
            body={
              "body" in col ? (col as { body?: (r: DirectoryUserShape) => string }).body : undefined
            }
            className={classNames({
              // exclusive conditions: to avoid conflicts and have multiple apply
              "min-w-48": aiStackFilterElements[col.field],
              "min-w-40": !aiStackFilterElements[col.field] && col.filterPlaceholder,
              "min-w-20": !aiStackFilterElements[col.field] && !col.filterPlaceholder,
            })}
          />
        ))}
      </DataTable>
    );

    return (
      <>
        {showNewHire && (
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Add New Hires</h4>
            <div className="inline-flex items-center flex-nowrap p-1">
              <Checkbox inputId="new-hire" checked={newHire} onChange={onNewHireChange} />
              <label
                htmlFor="new-hire"
                className="ml-2 flex-1 min-w-0 whitespace-normal break-words cursor-pointer"
              >
                Automatically add new users to this {entityType} when they are added to the platform
              </label>
            </div>
          </div>
        )}

        <div className="mb-3">
          <h4 className="font-semibold">Add Specific Users</h4>
          {hasPrepopulatedUsers ? (
            <>
              <p className="mb-2">
                You can add specific users to an assignment using the table below.
              </p>
              <p className="mb-0">
                Please note that adding or removing users below will not update the recommended
                lessons in this Assignment. To receive updated recommendations, generate a new
                Assignment.
              </p>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <p className="mb-0">
                You can add specific users to this {entityType} using the table and/or CSV file.
              </p>
              <Button label="CSV Upload" text onClick={() => setShowCsvUploadModal(true)} />
            </div>
          )}
        </div>

        <TabView
          aria-label="Audience user selection"
          activeIndex={activeIndex}
          onTabChange={handleTabChange}
        >
          <TabPanel header="User List">
            {renderTable(filteredAvailableUsers, selectedUsersToAdd, setSelectedUsersToAdd)}
          </TabPanel>
          <TabPanel header={`Users Added (${addedUsers.length})`}>
            {renderTable(addedUsers, selectedUsersToRemove, setSelectedUsersToRemove)}
          </TabPanel>
        </TabView>

        {!hasPrepopulatedUsers && (
          <CsvUploadModal
            open={showCsvUploadModal}
            onClose={() => setShowCsvUploadModal(false)}
            availableUsers={filteredAvailableUsers}
            onUsersAdded={newUsers => {
              const existingUuids = new Set(storedUsers.map(u => u.directoryUuid));
              const uniqueNew = newUsers.filter(u => !existingUuids.has(u.directoryUuid ?? ""));
              const newStored: StoredUser[] = [
                ...storedUsers,
                ...uniqueNew.map(u => ({
                  directoryUuid: u.directoryUuid ?? "",
                  email: u.email,
                })),
              ];
              handleChange({ ...values, users: newStored });
            }}
            addedUsers={addedUsers}
            allUsers={values.allUsers}
          />
        )}
      </>
    );
  }
);

AudienceUsersList.displayName = "AudienceUsersList";

export default AudienceUsersList;
